import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { persistAuthSession } from '../../utils/auth';
import { useProductContext } from '../../context/ProductContext';
import './Auth.css';

const EyeIcon = ({ visible }) =>
    visible ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );

const ChangePassword = () => {
    const navigate = useNavigate();
    const { refreshProducts, selectProduct } = useProductContext();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (event) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value
        });
    };

    const toggleShowPassword = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const validatePassword = (password) => {
        const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
        if (!regex.test(password)) {
            return 'Password must be at least 8 characters and include 1 uppercase, 1 number, and 1 special character.';
        }

        return null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        if (formData.newPassword !== formData.confirmPassword) {
            window.showToast('Passwords do not match', 'error');
            setLoading(false);
            return;
        }

        const policyError = validatePassword(formData.newPassword);
        if (policyError) {
            window.showToast(policyError, 'error');
            setLoading(false);
            return;
        }

        try {
            const response = await authAPI.changePassword(
                formData.currentPassword,
                formData.newPassword,
                formData.confirmPassword
            );

            if (response.data.success) {
                persistAuthSession(response.data.data);
                window.showToast(response.data.message, 'success');

                const userRole = response.data.data?.user?.role;

                if (userRole === 'SUPER_ADMIN') {
                    navigate('/dashboard', { replace: true });
                    return;
                }

                await refreshProducts();

                const assignedProducts = response.data.data?.user?.assignedProducts || [];

                if (assignedProducts.length === 1) {
                    selectProduct(assignedProducts[0]);
                    navigate('/main-menu', { replace: true });
                    return;
                }

                navigate('/product-selection', { replace: true });
            }
        } catch (error) {
            window.showToast(error.response?.data?.message || 'Failed to change password', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h2>Change Password</h2>
                <p className="auth-description">You must change your temporary password before continuing.</p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="currentPassword">Current Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword.currentPassword ? 'text' : 'password'}
                                id="currentPassword"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => toggleShowPassword('currentPassword')}
                                tabIndex={-1}
                                aria-label={showPassword.currentPassword ? 'Hide password' : 'Show password'}
                            >
                                <EyeIcon visible={showPassword.currentPassword} />
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword.newPassword ? 'text' : 'password'}
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                required
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => toggleShowPassword('newPassword')}
                                tabIndex={-1}
                                aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                            >
                                <EyeIcon visible={showPassword.newPassword} />
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword.confirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => toggleShowPassword('confirmPassword')}
                                tabIndex={-1}
                                aria-label={showPassword.confirmPassword ? 'Hide password' : 'Show password'}
                            >
                                <EyeIcon visible={showPassword.confirmPassword} />
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangePassword;
